import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import helpers from '~/Classes/helpers'
import i18n from '~/i18n'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import ButtonSimple from '~/Common-Objects/UI/Forms/ButtonSimple/ButtonSimple'
import Column from '~/Common-Objects/Grids/Column/Column'
import Hint from '~/Common-Objects/UI/Hint/Hint'
import Icons from '~/Common-Objects/UI/Icons/index'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Row from '~/Common-Objects/Grids/Row/Row'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import UpsellModal from '~/Common-Objects/UpsellModal'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import Duplicate from './duplicate'
import Delete from './destroy'
import { alterSiteDrawer, alterSnackbar, alterFullpage } from '~/data/actions/siteActions'
import axios from '~/data/http/axios'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'

export default function LandingPageTemplate({
    landingPage,
    LandingPageId,
    setLandingPage = () => {},
    saveLandingPage = () => {},
    saving = false,
    shortCode,
    domains,
    url,
    children,
    formsData = null
}) {
    const history = useHistory()
    const dispatch = useDispatch()
    const [tabIndex, setTabIndex] = useState(findTabIndex)
    const [editingName, setEditingName] = useState(false)
    const [newName, setNewName] = useState('')
    const { requiredTier } = useTierAllowed('landingPages')

    const findTabIndex = () => {
        const path = history.location.pathname.split('/')[4] || ''
        switch (path) {
            case 'analytics':
                return 1
            case 'visitors':
                return 2
            case 'settings':
                return 3
            default:
                return 0
        }
    }

    const showLandingPageUpsell = (required) => {
        dispatch(
            alterFullpage(
                <UpsellModal
                    feature='landingPages'
                    requiredTier={required ? required.name : requiredTier?.name || 'Starter'}
                    onUpgrade={() => dispatch(alterSiteDrawer(false, true, 'right', null))}
                />
            )
        )
    }

    const onStatusChange = async (e) => {
        const newStatus = e.target.value
        if (Number(newStatus) === 1 && Number(landingPage.status) !== 1) {
            try {
                const res = await axios.get('/accountMaster/tier/landingPages')
                if (!res.data.allowed) {
                    showLandingPageUpsell(res.data.requiredTier)
                    return
                }
            } catch (err) {
                console.log(err)
            }
        }
        saveLandingPage({ ...landingPage, status: newStatus })
    }

    const tabChange = (event, returnUrl) => {
        let index = typeof event === 'object' ? event.target.value : event
        let path = ''
        switch (index) {
            case 0:
                path = '/cp/landingpages/' + LandingPageId
                break
            case 1:
                path = '/cp/landingpages/' + LandingPageId + '/analytics'
                break
            case 2:
                path = '/cp/landingpages/' + LandingPageId + '/visitors'
                break
            case 3:
                path = '/cp/landingpages/' + LandingPageId + '/settings'
                break
        }
        if (returnUrl) return path
        if (path !== history.location.pathname) {
            history.push(path)
        }
    }

    const selectOption = (e) => {
        let drawer
        if (e.target.value === 'delete') {
            drawer = (
                <Delete
                    landingPage={landingPage}
                    onFinish={() => {
                        history.push('/cp/landingpages')
                    }}
                />
            )
        } else if (e.target.value === 'duplicate') {
            drawer = (
                <Duplicate
                    landingPage={landingPage}
                    onFinish={(newLandingPageId) => {
                        history.push(`/cp/templates/add/dragDropv2/?landingPageId=${newLandingPageId}`)
                    }}
                />
            )
        }
        if (drawer) {
            dispatch(alterSiteDrawer(true, true, 'right', drawer, true, 400))
        }
    }

    const confirmChangeName = async () => {
        if (!newName || newName === landingPage.formName) {
            setEditingName(false)
            setNewName('')
            return
        }
        if (newName.length > 255) {
            return dispatch(alterSnackbar(true, 'Name cannot be longer than 255 characters'))
        }
        if (newName.length < 3) {
            return dispatch(alterSnackbar(true, 'Name must be at least 3 characters'))
        }
        await saveLandingPage({ ...landingPage, formName: newName })
        setEditingName(false)
        setNewName('')
    }

    const automate = () => {
        dispatch(
            alterSiteDrawer(true, true, 'right', <Automate landingPage={landingPage} formData={formsData} />, true, 500)
        )
    }

    useEffect(() => {
        setTabIndex(findTabIndex)
    }, [history.location.pathname])

    useEffect(() => {
        if (tabIndex !== undefined) tabChange(tabIndex)
    }, [tabIndex])

    const menuOptions = [
        {
            label: 'Overview',
            index: 0
        },
        {
            label: 'Analytics',
            index: 1
        },
        {
            label: 'Visitors',
            index: 2
        },
        {
            label: 'Settings',
            index: 3
        }
    ]

    let finalUrl = url
    if (landingPage.status === 0) {
        finalUrl += '?preview=true'
    }

    return (
        <Paper>
            <div className='quickFlex mar-b24' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div className='quickFlex' style={{ gap: 14 }}>
                    <Hint
                        className='mar-0'
                        iconLeft={<Icons icon='arrowLeft' />}
                        onClick={() => history.push('/cp/landingpages')}
                    >
                        Back
                    </Hint>
                    {!editingName && (
                        <div className='quickFlex' style={{ gap: 8, alignItems: 'center' }}>
                            <h3>{landingPage?.formName}</h3>
                            <Icons
                                icon='pencil'
                                onClick={() => {
                                    setNewName(landingPage.formName)
                                    setEditingName(true)
                                }}
                            />
                        </div>
                    )}
                    {editingName && (
                        <div className='quickFlex' style={{ gap: 8, alignItems: 'center' }}>
                            <TextInput
                                focus
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder={landingPage.formName}
                                onEnterPress={confirmChangeName}
                                onEscapePress={() => {
                                    setEditingName(false)
                                    setNewName('')
                                }}
                            />
                            <Icons icon='tick' onClick={confirmChangeName} />
                        </div>
                    )}
                </div>
                {!!landingPage && (
                    <div className='quickFlex' style={{ gap: 8 }}>
                        <Select
                            buttonClass={landingPage.status ? 'button-green-outline' : 'button-grey-outline'}
                            overrideButtonClass={true}
                            buttonStyle={{ borderRadius: '20px' }}
                            fixedHeader={landingPage.status ? 'Public' : 'Draft'}
                            data={[
                                { label: 'Draft', value: 0 },
                                { label: 'Public', value: 1 }
                            ]}
                            onChange={onStatusChange}
                        ></Select>
                        <Button
                            iconRight={<Icons icon='external' />}
                            href={finalUrl}
                            target='_blank'
                            postHogTag='landing-page-overview-open'
                        >
                            Open Landing Page
                        </Button>
                        {formsData && formsData.length > 0 && (
                            <Button
                                iconLeft={<Icons icon='lightning' />}
                                postHogTag='landing-page-overview-automate'
                                onClick={automate}
                            >
                                Automate
                            </Button>
                        )}

                        <Select
                            buttonClass={'button-outline'}
                            overrideButtonClass={true}
                            hideIconRight={true}
                            buttonStyle={{ padding: '5px 10px' }}
                            fixedHeader={<Icons icon='dotsVertical' style={{ height: 16, width: 16 }} />}
                            data={[
                                {
                                    label: 'Duplicate',
                                    value: 'duplicate',
                                    iconLeft: 'copyClipboard',
                                    iconLeftColor: 'var(--neutral-600)'
                                },
                                { label: 'Delete', value: 'delete', iconLeft: 'bin', iconLeftColor: 'var(--red-500)' }
                            ]}
                            onChange={selectOption}
                        ></Select>
                    </div>
                )}
            </div>
            <Row className='mar-b24'>
                <Column xs={12} className='hide-md hide-lg' margin={0}>
                    <div className='form-group'>
                        <Select
                            data={menuOptions.map((o) => {
                                return {
                                    label: o.mobilelabel || o.label,
                                    value: o.index
                                }
                            })}
                            onChange={(e) => tabChange(e)}
                            value={tabIndex}
                        />
                    </div>
                </Column>
                <Column xs={12} className='hide-xs hide-sm show-md show-lg sub-nav-holder' margin={0}>
                    {menuOptions
                        .sort((a, b) => a.index - b.index)
                        .map((o) => {
                            return (
                                <ButtonSimple
                                    key={'tab_' + o.index}
                                    className='button-simple-fullsize sub-nav-button'
                                    selected={tabIndex === o.index}
                                    to={tabChange(o.index, true)}
                                >
                                    {o.label}
                                </ButtonSimple>
                            )
                        })}
                </Column>
            </Row>
            {children}
        </Paper>
    )
}

function Automate({ landingPage, formData }) {
    const site = useSelector((state) => state.siteMaster)
    const [formId, setFormId] = useState(formData[0].id)
    const [trigger, setTrigger] = useState('formSignup')
    const [contactType, setContactType] = useState('-1')
    const [name, setName] = useState(landingPage.formName + ' Signup Automation')
    const history = useHistory()

    const create = async () => {
        const res = await axios.post('/automation', {
            name,
            version: 1,
            steps: [
                {
                    guid: helpers.createLocalGuid(),
                    type: 'initial',
                    complete: true,
                    parentGuid: 0,
                    stepOptions: {
                        formId: { value: formId, required: true },
                        formType: { value: trigger, required: true },
                        formContactType: { value: contactType, required: true }
                    },
                    triggerType: { value: '17', required: true },
                    wizardComplete: true
                }
            ]
        })

        history.push('/cp/automation/addnew/' + res.data.Automation.id + '?redirect=/cp/landingpages/' + landingPage.id)
    }

    return (
        <div>
            <div className='quickFlex mar-b10' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>Create an new Automation</h4>
                {site.siteId === 1 && (
                    <Hint
                        href='https://transpond.io/support/automating-your-communications/transpond-automations'
                        iconLeft={
                            <Icons icon='questionCircle' color='#2B2F41' style={{ height: 16, width: 16 }} />
                        }
                    >
                        Learn More
                    </Hint>
                )}
            </div>

            <p className='mar-b25 text-sml'>
                You can start a new automation based on when contacts sign up using this Landing Page. <br />
                <br />
                Then perform lots of actions such as automatically sending the contact to a chain of emails.
            </p>

            {formData.length > 1 && (
                <Select
                    outerClassName='mar-b25'
                    label='Which form should trigger this automation?'
                    data={formData.map((f) => ({ label: f.formName, value: f.id }))}
                    value={formId}
                    onChange={(e) => setFormId(e.target.value)}
                />
            )}

            <Select
                outerClassName='mar-b25'
                label='Should this be on initial sign up or when they have confirmed their email?'
                data={[
                    {
                        label: i18n.t('automation:add.initial.formInitial'),
                        value: 'formSignup'
                    },
                    {
                        label: i18n.t('automation:add.initial.formConfirm'),
                        value: 'formConfirm'
                    }
                ]}
                value={trigger}
                onChange={(e) => setTrigger(e.target.value)}
            />

            <Select
                outerClassName='mar-b25'
                label='Do you want this to only apply to new contacts or also existing contacts?'
                data={[
                    {
                        label: i18n.t('automation:add.initial.formContactStatusBoth'),
                        value: '-1'
                    },
                    {
                        label: i18n.t('automation:add.initial.formContactStatusNew'),
                        value: 'newContact'
                    },
                    {
                        label: i18n.t('automation:add.initial.formContactStatusUpdate'),
                        value: 'updatedContact'
                    }
                ]}
                value={contactType}
                onChange={(e) => setContactType(e.target.value)}
            />

            {!!trigger && (
                <TextInput
                    label='Give this new Automation a name'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    outerClassName='mar-b25'
                />
            )}

            {!!trigger && !!name && (
                <>
                    <WarningHolder className='mar-b25'>
                        This will create the automation and take you to the automation designer so you can setup any
                        actions you want to happen when this automation is triggered
                    </WarningHolder>
                    <Button className='button-primary' onClick={create}>
                        Create Automation
                    </Button>
                </>
            )}
        </div>
    )
}
